import { useEffect } from "react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";

const SECTIONS = [
  {
    id: "information-we-collect",
    title: "Information We Collect",
    blocks: [
      "We collect information reasonably required to provide and improve the Service.",
      { h: "1.1 Account Information" },
      "When you register for TOTO, we may collect:",
      ["Name", "Email address", "Mobile number, where applicable", "Password or authentication credentials", "Profile information", "Account creation and login information"],
      "Passwords are not stored in plain text.",
    ],
  },
  {
    id: "visiting-card-information",
    title: "Visiting Card Information",
    blocks: [
      "TOTO allows you to photograph or upload visiting cards.",
      "When you scan a visiting card, the Service may process information visible on the card, including:",
      ["Person's name", "Designation", "Company/business name", "Phone number", "Alternate phone number", "WhatsApp number", "Email address", "Website", "Business address", "Area", "City", "State", "Country", "Postal code", "Business category", "Other information visible on the card"],
      "This information is processed to extract structured contact information and make it easier for you to save and manage business relationships.",
    ],
  },
  {
    id: "visiting-card-images",
    title: "Visiting Card Images",
    blocks: [
      "When you scan or upload a visiting card, TOTO may store the original card image associated with your account.",
      "Scanned card images are treated as private user data.",
      "They are not intended to be publicly accessible and are not made available to other TOTO users by default.",
    ],
  },
  {
    id: "ai-and-ocr-processing",
    title: "AI and OCR Processing",
    blocks: [
      "TOTO may use third-party artificial intelligence, optical character recognition (OCR), or image-processing services to extract information from visiting cards.",
      "The purpose of this processing is to convert information appearing on a card into structured contact information.",
      "Where third-party processing is used:",
      ["Data is transmitted only as necessary to perform the requested function.", "Service providers may process information on our behalf.", "Appropriate security and contractual safeguards are used where applicable.", "Information is not intentionally provided to service providers for unrelated purposes except where permitted by applicable law and contractual arrangements."],
      "TOTO will not intentionally ask an AI system to invent information that is not visible on the visiting card.",
    ],
  },
  {
    id: "contact-information",
    title: "Contact Information",
    blocks: [
      "Contacts saved within TOTO are associated with your account.",
      "Your contacts may include:",
      ["Names", "Companies", "Designations", "Phone numbers", "Email addresses", "Websites", "Business addresses", "Categories", "Notes", "Lead status", "Follow-up information", "Other information you choose to save"],
      "Your saved contacts are private by default.",
    ],
  },
  {
    id: "device-contacts",
    title: "Device Contacts",
    blocks: [
      "TOTO may provide an option to save a TOTO contact to your device's native contacts.",
      "TOTO requests access to device contacts only when required for a specific contact-related action.",
      "You may deny contact permission.",
      "If you do not provide contact permission, you can continue using other parts of TOTO that do not require access to your device contacts.",
      "TOTO does not access your device contacts simply because you installed the application.",
    ],
  },
  {
    id: "location-and-map-features",
    title: "Location and Map Features",
    blocks: [
      "TOTO may provide map-based functionality to help you explore businesses associated with your saved contacts.",
      "Business locations may be derived from addresses contained in visiting cards or information you provide.",
      "Where necessary, TOTO may use geocoding services to convert an address into geographic coordinates.",
      "Location information associated with your saved contacts is private by default.",
      "TOTO does not automatically make your private contact locations publicly available.",
      "If a feature allows you to make business information discoverable, appropriate controls will be provided and only information permitted by that feature will be made public.",
    ],
  },
  {
    id: "toto-goals",
    title: "TOTO Goals",
    blocks: [
      "TOTO includes a networking goals feature designed to help users develop networking habits and generate business opportunities.",
      "The Service may process information such as:",
      ["Number of new contacts", "Number of visiting cards scanned", "Follow-up activity", "Potential leads", "Goal completion", "Networking streaks", "Daily, weekly, and monthly progress"],
      "This information is used to provide your personal networking goals and progress.",
      "Your individual goals remain associated with your account and are not automatically made public.",
    ],
  },
  {
    id: "lead-and-business-information",
    title: "Lead and Business Information",
    blocks: [
      "You may classify contacts or business relationships for networking and lead-generation purposes.",
      "This may include:",
      ["Potential lead status", "Follow-up status", "Networking notes", "Business category", "Relationship-related information"],
      "You are responsible for ensuring that information you enter into TOTO is accurate and that you have an appropriate basis for storing or processing personal information belonging to other individuals.",
    ],
  },
  {
    id: "payment-and-subscription-information",
    title: "Payment and Subscription Information",
    blocks: [
      "TOTO may offer paid subscriptions.",
      "The current subscription structure may include: Premium: \u20B9199 per year.",
      "Payments may be processed through a third-party payment provider.",
      "Depending on the payment provider, TOTO may receive information such as:",
      ["Transaction ID", "Payment status", "Subscription status", "Purchase date", "Expiry date", "Payment-related metadata"],
      "TOTO does not intentionally store complete payment card numbers, CVV numbers, UPI PINs, or other sensitive payment authentication information.",
      "Payment information is handled by the relevant payment provider according to its privacy policy and security practices.",
    ],
  },
  {
    id: "usage-and-technical-information",
    title: "Usage and Technical Information",
    blocks: [
      "We may automatically collect limited technical information required to operate and secure the Service, including:",
      ["Device type", "Operating system", "App version", "Browser information where applicable", "IP address", "Login activity", "Error logs", "Crash information", "Performance information", "Approximate usage statistics"],
      "We use this information for security, troubleshooting, reliability, and service improvement.",
    ],
  },
  {
    id: "how-we-use-information",
    title: "How We Use Information",
    blocks: [
      "We may use information to:",
      ["Create and manage your account", "Authenticate users", "Process visiting-card images", "Extract information from visiting cards", "Create and manage contacts", "Store scanned card images", "Provide search and organization features", "Provide map and business exploration functionality", "Provide networking goals and progress", "Process subscriptions", "Provide customer support", "Prevent fraud and abuse", "Maintain security", "Diagnose technical problems", "Improve application performance", "Comply with legal obligations", "Enforce our Terms of Service"],
      "We do not sell your private contact database or scanned visiting-card images to third parties.",
    ],
  },
  {
    id: "your-private-data",
    title: "Your Private Data",
    blocks: [
      "TOTO is designed around user-controlled business networking data.",
      "Your private data may include:",
      ["Scanned visiting cards", "Card images", "Saved contacts", "Contact phone numbers", "Contact email addresses", "Private notes", "Lead information", "Follow-up information", "Private business locations"],
      "This information is private by default.",
      "We do not intentionally make this information available to other users merely because it exists in your TOTO account.",
    ],
  },
  {
    id: "admin-access",
    title: "Admin Access",
    blocks: [
      "TOTO may have a separate web-based Super Admin system for operating the Service.",
      "Administrators may have access to account and service-management information such as:",
      ["User account status", "Subscription status", "Subscription dates", "Aggregate scan statistics", "Aggregate goal statistics", "Goal completion statistics", "System usage information", "Account-related administrative information"],
      { h: "Important Privacy Restriction" },
      "TOTO administrators are not intended to have access to users' private:",
      ["Visiting-card images", "Saved contact records", "Contact phone numbers", "Contact email addresses", "Private notes", "Device contacts", "Private contact locations"],
      "Administrative permissions and APIs are designed to enforce this separation technically.",
      "Aggregate analytics may be used to understand overall service usage without exposing individual private contact information.",
    ],
  },
  {
    id: "data-sharing",
    title: "Data Sharing",
    blocks: [
      "We may share information with trusted third-party service providers when necessary to operate the Service.",
      "These providers may include:",
      ["Cloud hosting providers", "Database providers", "AI/OCR providers", "Image storage providers", "Map/geocoding providers", "Authentication providers", "Payment providers", "Analytics or crash-reporting providers", "Customer-support providers"],
      "These providers process information according to their role in providing services to TOTO and applicable contractual and legal requirements.",
      "We may also disclose information:",
      ["When required by law", "In response to valid legal processes", "To protect the rights, safety, and security of TOTO or users", "To investigate fraud, abuse, or security incidents", "In connection with a merger, acquisition, restructuring, or sale of relevant business assets, subject to applicable law"],
      "We do not sell users' private contact information or scanned card images.",
    ],
  },
  {
    id: "data-security",
    title: "Data Security",
    blocks: [
      "We take reasonable technical and organizational measures to protect information against:",
      ["Unauthorized access", "Unauthorized disclosure", "Accidental loss", "Destruction", "Alteration", "Misuse"],
      "Security measures may include:",
      ["Encrypted communications", "Authentication controls", "Access controls", "User-level data isolation", "Secure server-side processing", "Restricted administrative access", "Secure storage of uploaded files", "Environment-based secret management", "Server-side authorization"],
      "No method of electronic storage or transmission is completely secure. Therefore, we cannot guarantee absolute security.",
    ],
  },
  {
    id: "data-retention",
    title: "Data Retention",
    blocks: [
      "We retain information for as long as reasonably necessary to:",
      ["Provide the Service", "Maintain your account", "Provide features you request", "Maintain transaction records", "Meet legal obligations", "Resolve disputes", "Prevent fraud and abuse", "Enforce agreements"],
      "Retention periods may differ depending on the type of information.",
      "When information is no longer required, we may delete, anonymize, or securely dispose of it in accordance with applicable law and our retention practices.",
    ],
  },
  {
    id: "account-deletion",
    title: "Account Deletion",
    blocks: [
      "You may request deletion of your TOTO account through the account-management functionality provided by the Service, where available.",
      "Upon account deletion, we will take reasonable steps to delete or anonymize information associated with your account, subject to:",
      ["Legal retention requirements", "Legitimate security requirements", "Outstanding transactions or disputes", "Backup systems that may retain information temporarily before scheduled deletion"],
      "Account deletion may result in permanent loss of:",
      ["Saved contacts", "Scanned visiting-card images", "Networking goals", "Lead information", "Account history"],
      "You should preserve information you need before deleting your account where such functionality is available.",
    ],
  },
  {
    id: "your-rights",
    title: "Your Rights",
    blocks: [
      "Depending on applicable law and your location, you may have rights regarding your personal information, including the right to:",
      ["Request access to personal information", "Request correction of inaccurate information", "Request deletion of information where legally permitted", "Request restriction of certain processing", "Withdraw consent where processing is based on consent", "Object to certain processing", "Request information about how your data is processed", "Raise a complaint with the appropriate data-protection authority"],
      "Applicable requests may be subject to identity verification and other requirements permitted by law.",
    ],
  },
  {
    id: "childrens-privacy",
    title: "Children's Privacy",
    blocks: [
      "TOTO is intended for business networking and professional use.",
      "The Service is not directed toward children.",
      "We do not knowingly collect personal information from children in violation of applicable law.",
      "If we become aware that personal information has been collected from a child in circumstances where such collection is not permitted, we will take reasonable steps to address the situation.",
    ],
  },
  {
    id: "cookies-and-similar-technologies",
    title: "Cookies and Similar Technologies",
    blocks: [
      "The TOTO website or web-based services may use cookies or similar technologies for:",
      ["Authentication", "Security", "Session management", "Preferences", "Performance", "Analytics where applicable"],
      "You may be able to control cookies through your browser settings.",
      "Disabling certain cookies may affect functionality.",
    ],
  },
  {
    id: "third-party-services",
    title: "Third-Party Services",
    blocks: [
      "TOTO may contain integrations with third-party services.",
      "Third-party services operate under their own terms and privacy policies.",
      "TOTO is not responsible for the privacy practices of third-party services that operate independently from TOTO.",
      "Users should review the privacy policies of relevant third-party providers where appropriate.",
    ],
  },
  {
    id: "international-data-transfers",
    title: "International Data Transfers",
    blocks: [
      "Depending on the service providers used by TOTO, your information may be processed or stored in countries other than your country of residence.",
      "Where applicable, reasonable steps will be taken to ensure that such transfers comply with applicable data-protection requirements.",
    ],
  },
  {
    id: "changes-to-this-privacy-policy",
    title: "Changes to This Privacy Policy",
    blocks: [
      "We may update this Privacy Policy from time to time.",
      "When material changes are made, we may notify users through the application, website, email, or another appropriate method.",
      "The updated Privacy Policy will indicate its effective date.",
      "Your continued use of TOTO after an updated Privacy Policy becomes effective constitutes use subject to the updated policy, to the extent permitted by law.",
    ],
  },
  {
    id: "governing-principle",
    title: "Governing Principle",
    blocks: [
      "TOTO is built around a simple principle: Every Card Matters.",
      "The visiting cards and business relationships you save in TOTO belong to your networking workspace and are private by default.",
      "We use information to provide the features you request, including scanning cards, extracting information, managing contacts, supporting networking goals, and helping you build meaningful business relationships.",
      "We aim to collect only information reasonably necessary to provide the Service and protect it through appropriate security controls.",
      "Every Card Matters. Your Privacy Matters Too.",
    ],
  },
  {
    id: "contact-us",
    title: "Contact Us",
    blocks: [
      "If you have any questions about this Privacy Policy or how TOTO handles your data, contact us at:",
      { email: "info@kensworth.in" },
    ],
  },
];

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="privacy-policy-page">
      <Header />
      <main className="mx-auto max-w-3xl px-4 pb-24 pt-32 sm:px-6 sm:pt-40">
        <span className="text-xs font-semibold uppercase tracking-widest text-toto-blue" data-testid="privacy-eyebrow">TOTO — Every Card Matters</span>
        <h1 className="mt-3 font-heading text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl" data-testid="privacy-heading">
          Privacy Policy
        </h1>
        <p className="mt-4 text-sm text-slate-500" data-testid="privacy-last-updated">
          Effective Date: 14 September 2026 &middot; Last Updated: 14 September 2026
        </p>
        <div className="mt-6 space-y-4">
          <p className="text-base leading-relaxed text-slate-600">
            This Privacy Policy explains how TOTO (&ldquo;TOTO&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;) collects, uses, stores, processes, and protects information when you use the TOTO mobile application, website, and related services (collectively, the &ldquo;Service&rdquo;).
          </p>
          <p className="text-base leading-relaxed text-slate-600">
            TOTO is a business networking application that allows users to scan visiting cards, extract business information, save contacts, organize networking relationships, set networking goals, and explore businesses based on information associated with saved contacts.
          </p>
          <p className="text-base leading-relaxed text-slate-600">
            By creating an account or using TOTO, you acknowledge that you have read and understood this Privacy Policy.
          </p>
        </div>
        <div className="mt-12 space-y-12">
          {(() => {
            let secNum = 0;
            return SECTIONS.map((s) => {
              if (!s.unnumbered) secNum += 1;
              const num = secNum;
              return (
            <section key={s.id} data-testid={`privacy-section-${s.id}`}>
              <h2 className="font-heading text-xl font-semibold text-slate-900">
                {s.unnumbered ? s.title : `${num}. ${s.title}`}
              </h2>
              <div className="mt-3 space-y-3">
                {s.blocks.map((b, j) => {
                  if (Array.isArray(b)) {
                    return (
                      <ul key={j} className="list-disc space-y-1.5 pl-6 text-base leading-relaxed text-slate-600">
                        {b.map((item, k) => (
                          <li key={k}>{item}</li>
                        ))}
                      </ul>
                    );
                  }
                  if (typeof b === "object" && b.ol) {
                    return (
                      <ol key={j} className="list-decimal space-y-1.5 pl-6 text-base leading-relaxed text-slate-600">
                        {b.ol.map((item, k) => (
                          <li key={k}>{item}</li>
                        ))}
                      </ol>
                    );
                  }
                  if (typeof b === "object" && b.h) {
                    return (
                      <h3 key={j} className="pt-2 font-heading text-base font-semibold text-slate-800">
                        {b.h}
                      </h3>
                    );
                  }
                  if (typeof b === "object" && b.email) {
                    return (
                      <p key={j} className="text-base leading-relaxed text-slate-600">
                        Email:{" "}
                        <a href={`mailto:${b.email}`} className="font-semibold text-toto-blue hover:underline" data-testid="privacy-contact-email">
                          {b.email}
                        </a>
                      </p>
                    );
                  }
                  return (
                    <p key={j} className="text-base leading-relaxed text-slate-600">
                      {b}
                    </p>
                  );
                })}
              </div>
            </section>
              );
            });
          })()}
        </div>
      </main>
      <Footer />
    </div>
  );
}
